/**
 * PromoCodesAdmin.tsx — Admin > Promo Codes.
 *
 * The account's promo codes, laid out like every Admin list (Lead Sources is
 * the reference — components/admin/AdminList.tsx) with the corp dashboard's
 * Promo Codes columns, less Account: Code (with the title and the Active /
 * Scheduled / Expired badge beside it, as corp), Discount, Apply To,
 * Available To, Dates, Uses — each sorting from its title (by code to start).
 *   - "Add Promo Code" top right; search and Show deleted in the toolbar
 *     (corp's only other filter is Account, which one account has no use for);
 *   - clicking a live row opens its edit form; Edit / Delete icons on a live
 *     row, Restore on a deleted one; delete asks first.
 *
 * Phones (SCRUM-1468) keep their own treatment: the page releases the
 * viewport lock and clears the browser chrome (the wrapper around
 * AdminListPage), and the table scrolls sideways at a computed minimum width
 * instead of collapsing its columns (the wrapper around AdminListTable).
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live code still opens, as a viewer with a single Close
 * and no way to take a product type or category off it.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, AlertTriangle, CalendarDays, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { OutlineBadge, type BadgeTone } from '@/components/admin/CorpBadges';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareNumber, compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { toast } from '@/store/toastStore';
import * as adminSvc from '@/services/admin.service';
import type { PromoCode } from '@/services/admin.service';
import * as paymentsSvc from '@/services/payments.service';
import type { PaymentDiscount } from '@/services/payments.service';
import * as inventorySvc from '@/services/inventory.service';
import type { ProductTypeLookupEntry, ProductCategoryLookupEntry } from '@/services/inventory.service';
import { DatePicker } from '@/components/ui/DatePicker';

// ─── Constants ──────────────────────────────────────────────────────────────────

const APPLY_TO_LABELS: Record<number, string> = {
  0: 'All Products',
  1: 'Select Products',
};

const AVAILABLE_TO_LABELS: Record<number, string> = {
  0: 'Everyone',
  1: 'Non-Members',
  2: 'Members',
};

const TYPE_OPTIONS = [
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'date-range', label: 'Date Range' },
];

const APPLY_TO_OPTIONS = [
  { value: '0', label: 'All Products' },
  { value: '1', label: 'Select Products' },
];

const AVAILABLE_TO_OPTIONS = [
  { value: '0', label: 'Everyone' },
  { value: '1', label: 'Non-Members' },
  { value: '2', label: 'Members' },
];

/*
 * The corp Promo Codes columns (less Account), in corp order. The percentage
 * widths are the ones desktop rendered before this page moved onto the shared
 * list, and the phone minimum below is computed from them: the tracks take
 * 0.59W, Actions a fixed 128px (ACTIONS_COLUMN's w-32), and Code the rest. The
 * binding constraint is Dates — "Jun 17, 2026 – Jun 26, 2026" at text-xs plus
 * a 12px icon, a gap-1 and 24px of px-3 needs ~180px at 16% — so W >= ~1125;
 * at 1140 Dates is 182px and Code 339px. Cells are ADMIN_TD_CLASS (px-4) like
 * every Admin list, except that the Dates cell keeps px-3 below md, so this
 * arithmetic still holds on phones.
 */
const COLUMNS: AdminListColumn[] = [
  { key: 'code', header: 'Code', sortable: true },
  { key: 'discount', header: 'Discount', className: 'w-[15%]', sortable: true },
  { key: 'applyTo', header: 'Apply To', className: 'w-[10%]', sortable: true },
  { key: 'availableTo', header: 'Available To', className: 'w-[10%]', sortable: true },
  { key: 'dates', header: 'Dates', className: 'w-[16%]', sortable: true },
  { key: 'uses', header: 'Uses', className: 'w-[8%]', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** Code A–Z: the list's own order, as corp. */
function compareCode(a: PromoCode, b: PromoCode): number {
  return a.promoCode.localeCompare(b.promoCode, undefined, { sensitivity: 'base' });
}

/** A date range sorts by its start, then its end; "No expiration" after every range. */
function datesKey(row: PromoCode): string {
  return row.type === 'date-range' ? `${row.startDate ?? ''}|${row.stopDate ?? ''}` : '￿';
}

// ─── Form state ─────────────────────────────────────────────────────────────────

interface PromoForm {
  promoCode: string;
  promoTitle: string;
  description: string;
  paymentDiscountID: string;
  type: string;
  applyTo: string;
  availableTo: string;
  startDate: string;
  stopDate: string;
  maxUses: string;
  patientUses: string;
  productTypeIds: number[];
  productCategoryIds: number[];
}

const EMPTY_FORM: PromoForm = {
  promoCode: '',
  promoTitle: '',
  description: '',
  paymentDiscountID: '',
  type: 'unlimited',
  applyTo: '0',
  availableTo: '0',
  startDate: '',
  stopDate: '',
  maxUses: '0',
  patientUses: '0',
  productTypeIds: [],
  productCategoryIds: [],
};

// ─── Modal ──────────────────────────────────────────────────────────────────────

interface PromoModalProps {
  promo?: PromoCode | null;
  discounts: PaymentDiscount[];
  productTypes: ProductTypeLookupEntry[];
  productCategories: ProductCategoryLookupEntry[];
  allPromos: PromoCode[];
  onClose: () => void;
  onSaved: (saved: PromoCode, isNew: boolean) => void;
}

function PromoModal({
  promo,
  discounts,
  productTypes,
  productCategories,
  allPromos,
  onClose,
  onSaved,
}: PromoModalProps) {
  const isEdit = promo != null;
  // Locked: a viewer — fields read-only, nothing to remove, Close only.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<PromoForm>(() =>
    isEdit
      ? {
          promoCode: promo.promoCode,
          promoTitle: promo.promoTitle ?? '',
          description: promo.description ?? '',
          paymentDiscountID: String(promo.paymentDiscountID),
          type: promo.type || 'unlimited',
          applyTo: String(promo.applyTo),
          availableTo: String(promo.availableTo ?? 0),
          startDate:
            promo.startDate && promo.startDate !== '0000-00-00 00:00:00'
              ? promo.startDate.slice(0, 10)
              : '',
          stopDate:
            promo.stopDate && promo.stopDate !== '0000-00-00 00:00:00'
              ? promo.stopDate.slice(0, 10)
              : '',
          maxUses: String(promo.maxUses),
          patientUses: String(promo.patientUses),
          productTypeIds: [],
          productCategoryIds: [],
        }
      : EMPTY_FORM,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (!isEdit || promo.applyTo !== 1) return;
    setLoadingProducts(true);
    Promise.all([
      adminSvc.listPromoCodeProducts(promo.promoCodeID),
      adminSvc.listPromoCodeProductCategories(promo.promoCodeID),
    ])
      .then(([products, categories]) => {
        setForm((f) => ({
          ...f,
          productTypeIds: products.map((p) => p.productTypeID),
          productCategoryIds: categories.map((c) => c.productCategoryID),
        }));
      })
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, [isEdit, promo?.promoCodeID, promo?.applyTo]);

  function set<K extends keyof PromoForm>(field: K, value: PromoForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const discountOptions = useMemo(() => {
    return discounts
      .filter((d) => d.active && d.discountType !== 3 && d.discountType !== 4)
      .map((d) => ({ value: String(d.paymentDiscountID), label: d.title }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [discounts]);

  const productTypeOptions = useMemo(() => {
    return productTypes
      .map((pt) => ({ value: String(pt.productTypeID), label: pt.productTypeName ?? `#${pt.productTypeID}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [productTypes]);

  const productCategoryOptions = useMemo(() => {
    return productCategories
      .map((c) => ({ value: String(c.productCategoryID), label: c.productCategoryName ?? `#${c.productCategoryID}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [productCategories]);

  const promoCodeTaken = useMemo(() => {
    const code = form.promoCode.trim().toLowerCase();
    if (code.length < 6) return false;
    return allPromos.some((p) => {
      if (isEdit && p.promoCodeID === promo.promoCodeID) return false;
      return p.promoCode.trim().toLowerCase() === code && p.status === 1;
    });
  }, [form.promoCode, allPromos, isEdit, promo?.promoCodeID]);

  const errors = useMemo(() => {
    const msgs: string[] = [];
    if (!form.promoCode.trim()) msgs.push('Promo code is required');
    else if (form.promoCode.trim().length < 6) msgs.push('Promo code must be at least 6 characters');
    else if (promoCodeTaken) msgs.push('This promo code is already in use');
    if (!form.promoTitle.trim()) msgs.push('Title is required');
    if (!form.paymentDiscountID) msgs.push('Payment discount is required');
    if (form.type === 'date-range') {
      if (!form.startDate) msgs.push('Start date is required');
      if (!form.stopDate) msgs.push('Stop date is required');
    }
    if (form.applyTo === '1' && form.productTypeIds.length === 0 && form.productCategoryIds.length === 0) {
      msgs.push('Select at least one product type or category');
    }
    return msgs;
  }, [form, promoCodeTaken]);

  const warnings = useMemo(() => {
    const msgs: string[] = [];
    if (
      form.type === 'date-range' &&
      form.startDate &&
      form.stopDate &&
      form.stopDate < form.startDate
    ) {
      msgs.push('Stop date is before start date');
    }
    return msgs;
  }, [form]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (errors.length > 0 || locked) return;

    const payload: Record<string, unknown> = {
      promoCode: form.promoCode.trim(),
      promoTitle: form.promoTitle.trim(),
      description: form.description.trim(),
      paymentDiscountID: Number(form.paymentDiscountID),
      type: form.type,
      applyTo: Number(form.applyTo),
      availableTo: Number(form.availableTo),
      maxUses: parseInt(form.maxUses) || 0,
      patientUses: parseInt(form.patientUses) || 0,
      productTypeIds: form.applyTo === '1' ? form.productTypeIds : [],
    };
    if (form.type === 'date-range') {
      payload.startDate = form.startDate;
      payload.stopDate = form.stopDate;
    }

    setSaving(true);
    setError(null);
    try {
      let saved: PromoCode;
      if (isEdit) {
        saved = await adminSvc.updatePromoCode(promo.promoCodeID, payload);
        const ptIds = form.applyTo === '1' ? form.productTypeIds : [];
        await adminSvc.setPromoCodeProducts(promo.promoCodeID, ptIds);
        const catIds = form.applyTo === '1' ? form.productCategoryIds : [];
        await adminSvc.setPromoCodeProductCategories(promo.promoCodeID, catIds);
      } else {
        saved = await adminSvc.createPromoCode(payload);
        if (saved && form.applyTo === '1') {
          const newId = (saved as any).promoCodeID ?? (saved as any).id;
          if (newId) {
            if (form.productTypeIds.length > 0) {
              await adminSvc.setPromoCodeProducts(newId, form.productTypeIds);
            }
            if (form.productCategoryIds.length > 0) {
              await adminSvc.setPromoCodeProductCategories(newId, form.productCategoryIds);
            }
          }
        }
      }
      toast.success(isEdit ? 'Promo code updated' : 'Promo code created');
      onSaved(saved, !isEdit);
      onClose();
    } catch {
      setError('Failed to save promo code. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Promo Code' : isEdit ? 'Edit Promo Code' : 'Add Promo Code'}
      size="lg"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={saving || errors.length > 0}
            loading={saving}
          >
            {isEdit ? 'Save Changes' : 'Add Promo Code'}
          </Button>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          {errors.length > 0 && !locked && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium text-red-700 mb-1">Required before saving:</p>
              <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
                {errors.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Promo Code" required={!locked}>
              <Input
                value={form.promoCode}
                onChange={(e) => set('promoCode', e.target.value)}
                placeholder="e.g. SUMMER2026 (min 6 characters)"
                required
                autoFocus
                error={
                  locked
                    ? undefined
                    : promoCodeTaken
                    ? 'Already in use'
                    : form.promoCode.trim().length > 0 && form.promoCode.trim().length < 6
                      ? `${form.promoCode.trim().length}/6 characters`
                      : undefined
                }
              />
            </FormField>

            <FormField label="Title" required={!locked}>
              <Input
                value={form.promoTitle}
                onChange={(e) => set('promoTitle', e.target.value)}
                placeholder="e.g. Summer Sale 2026"
                required
              />
            </FormField>
          </div>

          <FormField label="Description">
            <Textarea autoResize
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </FormField>

          <FormField label="Payment Discount" required={!locked}>
            <Select
              options={discountOptions}
              value={form.paymentDiscountID}
              onChange={(val) => set('paymentDiscountID', val ?? '')}
              placeholder="Select discount..."
              searchable
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Type">
              <Select
                options={TYPE_OPTIONS}
                value={form.type}
                onChange={(val) => {
                  if (!val) return;
                  setForm((f) => ({
                    ...f,
                    type: val,
                    ...(val === 'unlimited' ? { startDate: '', stopDate: '' } : {}),
                  }));
                }}
              />
            </FormField>

            <FormField label="Available To">
              <Select
                options={AVAILABLE_TO_OPTIONS}
                value={form.availableTo}
                onChange={(val) => set('availableTo', val ?? '0')}
              />
            </FormField>
          </div>

          {form.type === 'date-range' && (
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Start Date" required={!locked} htmlFor="promo-start-date">
                <DatePicker
                  id="promo-start-date"
                  value={form.startDate}
                  onChange={(value) => set('startDate', value)}
                />
              </FormField>
              <FormField label="Stop Date" required={!locked} htmlFor="promo-stop-date">
                <DatePicker
                  id="promo-stop-date"
                  value={form.stopDate}
                  onChange={(value) => set('stopDate', value)}
                />
              </FormField>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Max Uses">
              <Input
                type="number"
                min="0"
                value={form.maxUses}
                onChange={(e) => set('maxUses', e.target.value)}
                placeholder="0 = unlimited"
              />
              <p className="text-xs text-slate-400 mt-1">
                Limit the total number of times this promo code can be used. 0 = unlimited.
              </p>
            </FormField>
            <FormField label="Uses Per Patient">
              <Input
                type="number"
                min="0"
                value={form.patientUses}
                onChange={(e) => set('patientUses', e.target.value)}
                placeholder="0 = unlimited"
              />
              <p className="text-xs text-slate-400 mt-1">
                Limit how many times each patient can use this promo code. 0 = unlimited per patient.
              </p>
            </FormField>
          </div>

          <FormField label="Apply To">
            <Select
              options={APPLY_TO_OPTIONS}
              value={form.applyTo}
              onChange={(val) => set('applyTo', val ?? '0')}
            />
          </FormField>

          {form.applyTo === '1' && (
            <div className="rounded-md border border-slate-200 p-3 space-y-4">
              {loadingProducts ? (
                <p className="text-sm text-slate-500 py-2">Loading product data…</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Product Types</p>
                    {productTypeOptions.length === 0 ? (
                      <p className="text-sm text-slate-400 py-2">No product types available.</p>
                    ) : (
                      <>
                        <SearchableMultiSelect
                          options={productTypeOptions}
                          selectedValues={form.productTypeIds.map(String)}
                          onSelectionChange={(ids) =>
                            set('productTypeIds', ids.map(Number))
                          }
                          placeholder="Select product types..."
                          searchPlaceholder="Search product types..."
                        />
                        {form.productTypeIds.length > 0 && (
                          <div className="space-y-1">
                            {form.productTypeIds.map((ptId) => {
                              const opt = productTypeOptions.find(
                                (o) => o.value === String(ptId),
                              );
                              if (!opt) return null;
                              return (
                                <div
                                  key={ptId}
                                  className="flex items-center gap-1 text-xs bg-white border border-slate-200 rounded px-2 py-1"
                                >
                                  <span className="flex-1 truncate">{opt.label}</span>
                                  {!locked && (
                                    <button
                                      type="button"
                                      className="text-slate-400 hover:text-red-600 shrink-0"
                                      onClick={() =>
                                        set(
                                          'productTypeIds',
                                          form.productTypeIds.filter((id) => id !== ptId),
                                        )
                                      }
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Product Categories</p>
                    {productCategoryOptions.length === 0 ? (
                      <p className="text-sm text-slate-400 py-2">No product categories available.</p>
                    ) : (
                      <>
                        <SearchableMultiSelect
                          options={productCategoryOptions}
                          selectedValues={form.productCategoryIds.map(String)}
                          onSelectionChange={(ids) =>
                            set('productCategoryIds', ids.map(Number))
                          }
                          placeholder="Select product categories..."
                          searchPlaceholder="Search product categories..."
                        />
                        {form.productCategoryIds.length > 0 && (
                          <div className="space-y-1">
                            {form.productCategoryIds.map((catId) => {
                              const opt = productCategoryOptions.find(
                                (o) => o.value === String(catId),
                              );
                              if (!opt) return null;
                              return (
                                <div
                                  key={catId}
                                  className="flex items-center gap-1 text-xs bg-white border border-slate-200 rounded px-2 py-1"
                                >
                                  <span className="flex-1 truncate">{opt.label}</span>
                                  {!locked && (
                                    <button
                                      type="button"
                                      className="text-slate-400 hover:text-red-600 shrink-0"
                                      onClick={() =>
                                        set(
                                          'productCategoryIds',
                                          form.productCategoryIds.filter((id) => id !== catId),
                                        )
                                      }
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {warnings.length > 0 && !locked && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-700">
                {warnings.map((w, i) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            </div>
          )}
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function isDeleted(p: PromoCode): boolean {
  return p.status !== 1;
}

function getPromoStatus(item: PromoCode): 'active' | 'scheduled' | 'expired' | 'deleted' {
  if (item.status !== 1) return 'deleted';
  if (item.type === 'unlimited' || !item.startDate || item.startDate === '0000-00-00 00:00:00') {
    return 'active';
  }
  const now = new Date();
  const start = new Date(item.startDate);
  const stop = new Date(item.stopDate);
  if (now < start) return 'scheduled';
  if (now > stop) return 'expired';
  return 'active';
}

/** Corp's status badge beside a live code: Active green, Scheduled blue, Expired grey. */
const STATUS_BADGE: Record<'active' | 'scheduled' | 'expired', { label: string; tone: BadgeTone }> = {
  active: { label: 'Active', tone: 'green' },
  scheduled: { label: 'Scheduled', tone: 'blue' },
  expired: { label: 'Expired', tone: 'gray' },
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr || dateStr === '0000-00-00 00:00:00') return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Main component ────────────────────────────────────────────────────────────

export function PromoCodesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [allItems, setAllItems] = useState<PromoCode[]>([]);
  const [discounts, setDiscounts] = useState<PaymentDiscount[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeLookupEntry[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategoryLookupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<PromoCode | null>(null);
  const [deleteItem, setDeleteItem] = useState<PromoCode | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [promos, discs, pts, cats] = await Promise.all([
        adminSvc.listPromoCodes(),
        paymentsSvc.listDiscounts(),
        inventorySvc.listProductTypes(),
        inventorySvc.listProductCategoriesLookup(),
      ]);
      setAllItems(promos ?? []);
      setDiscounts(discs ?? []);
      setProductTypes(pts ?? []);
      setProductCategories(cats ?? []);
    } catch {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allItems
      .filter((item) => {
        if (!showDeleted && isDeleted(item)) return false;
        if (
          q &&
          !item.promoCode.toLowerCase().includes(q) &&
          !(item.promoTitle ?? '').toLowerCase().includes(q) &&
          !(item.description ?? '').toLowerCase().includes(q)
        ) {
          return false;
        }
        return true;
      })
      // Corp lists codes alphabetically.
      .sort(compareCode);
  }, [allItems, searchQuery, showDeleted]);

  const discountLabel = useCallback(
    (paymentDiscountID: number) => {
      const d = discounts.find((disc) => disc.paymentDiscountID === paymentDiscountID);
      return d?.title ?? `#${paymentDiscountID}`;
    },
    [discounts],
  );

  // Opens in code order (above); the other titles re-sort it, ties keeping it.
  const sortCompare = useMemo<ListSortCompare<PromoCode>>(
    () => ({
      code: compareCode,
      discount: (a, b) => compareText(discountLabel(a.paymentDiscountID), discountLabel(b.paymentDiscountID)),
      applyTo: (a, b) => compareText(APPLY_TO_LABELS[a.applyTo] ?? 'Unknown', APPLY_TO_LABELS[b.applyTo] ?? 'Unknown'),
      availableTo: (a, b) =>
        compareText(AVAILABLE_TO_LABELS[a.availableTo] ?? 'Everyone', AVAILABLE_TO_LABELS[b.availableTo] ?? 'Everyone'),
      dates: (a, b) => (datesKey(a) < datesKey(b) ? -1 : datesKey(a) > datesKey(b) ? 1 : 0),
      uses: (a, b) => compareNumber(a.currentUses, b.currentUses),
    }),
    [discountLabel],
  );
  const sorting = useListSort(visibleItems, { initialKey: 'code', compare: sortCompare });
  const pagination = useListPagination(sorting.sorted, { resetOn: [searchQuery, showDeleted] });

  function handleCreated(saved: PromoCode) {
    setAllItems((prev) => [...prev, saved]);
  }

  function handleUpdated(saved: PromoCode) {
    setAllItems((prev) =>
      prev.map((p) => (p.promoCodeID === saved.promoCodeID ? { ...p, ...saved } : p)),
    );
    setEditItem(null);
  }

  function handleSaved(saved: PromoCode, isNew: boolean) {
    if (isNew) {
      handleCreated(saved);
    } else {
      handleUpdated(saved);
    }
  }

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await adminSvc.deletePromoCode(deleteItem.promoCodeID);
      setAllItems((prev) =>
        prev.map((p) =>
          p.promoCodeID === deleteItem.promoCodeID ? { ...p, status: 0 } : p,
        ),
      );
      toast.success('Promo code deleted');
    } catch {
      toast.error('Failed to delete promo code');
    }
    setDeleteItem(null);
  }

  async function handleRestore(item: PromoCode) {
    try {
      await adminSvc.restorePromoCode(item.promoCodeID);
      setAllItems((prev) =>
        prev.map((p) =>
          p.promoCodeID === item.promoCodeID ? { ...p, status: 1 } : p,
        ),
      );
      toast.success('Promo code restored');
    } catch {
      toast.error('Failed to restore promo code');
    }
  }

  return (
    <div className="h-full max-md:h-auto max-md:overflow-visible max-md:pb-16">
      <AdminListPage
        title="Promo Codes"
        subtitle="Manage promotional codes for your account."
        actions={
          <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
            Add Promo Code
          </Button>
        }
        toolbar={
          <AdminListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search promo codes…"
            searchLabel="Search promo codes"
            showDeleted={showDeleted}
            onShowDeletedChange={setShowDeleted}
            showDeletedId="promo-codes-show-deleted"
          />
        }
      >
        {loading ? (
          <SpinnerOverlay label="Loading promo codes…" />
        ) : visibleItems.length === 0 ? (
          <AdminListEmpty
            title="No promo codes found"
            hint={searchQuery.trim() ? 'Try adjusting your search.' : locked ? undefined : 'Add your first promo code to get started.'}
          />
        ) : (
          /* Phones: the table keeps its columns and scrolls sideways at the
             minimum computed above COLUMNS, and the box stops filling the
             remaining height under a single row. Desktop is untouched. */
          <div className="flex-1 min-h-0 flex flex-col max-md:flex-none max-md:overflow-x-auto">
            <div className="flex-1 min-h-0 flex flex-col max-md:min-w-[1140px]">
              <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Promo codes" sort={sorting.sort} onSortChange={sorting.onSortChange}>
                {pagination.pageItems.map((row) => {
                  const deleted = isDeleted(row);
                  const status = getPromoStatus(row);
                  const badge = status === 'deleted' ? null : STATUS_BADGE[status];
                  const muted = deleted ? 'text-slate-400' : 'text-slate-700';
                  return (
                    <tr
                      key={row.promoCodeID}
                      data-promo-code-id={row.promoCodeID}
                      className={adminRowClass({ deleted, clickable: !deleted })}
                      // A live row opens its form; a deleted one is restored, not edited.
                      onClick={deleted ? undefined : () => setEditItem(row)}
                    >
                      <td className={ADMIN_TD_CLASS}>
                        <AdminRowName
                          deleted={deleted}
                          nameTestId="promo-code"
                          suffix={
                            <>
                              {row.promoTitle && (
                                <span className={`min-w-0 truncate text-xs ${deleted ? 'text-slate-400' : 'text-slate-500'}`}>
                                  — {row.promoTitle}
                                </span>
                              )}
                              {badge && <OutlineBadge tone={badge.tone}>{badge.label}</OutlineBadge>}
                            </>
                          }
                        >
                          <span className="font-mono">{row.promoCode}</span>
                        </AdminRowName>
                      </td>
                      <td className={ADMIN_TD_CLASS}>
                        <span className={`text-sm ${muted}`}>{discountLabel(row.paymentDiscountID)}</span>
                      </td>
                      <td className={ADMIN_TD_CLASS}>
                        <span className={`text-sm ${muted}`}>{APPLY_TO_LABELS[row.applyTo] ?? 'Unknown'}</span>
                      </td>
                      <td className={ADMIN_TD_CLASS}>
                        <span className={`text-sm ${muted}`}>{AVAILABLE_TO_LABELS[row.availableTo] ?? 'Everyone'}</span>
                      </td>
                      <td className={cn(ADMIN_TD_CLASS, 'max-md:px-3')}>
                        {row.type === 'date-range' ? (
                          <div className={`text-xs flex items-center gap-1 ${deleted ? 'text-slate-400' : 'text-slate-600'}`}>
                            <CalendarDays className="h-3 w-3 shrink-0" />
                            {formatDate(row.startDate)} – {formatDate(row.stopDate)}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No expiration</span>
                        )}
                      </td>
                      <td className={ADMIN_TD_CLASS}>
                        <span className={`text-sm ${muted}`}>
                          {row.currentUses}/{row.maxUses === 0 ? '∞' : row.maxUses}
                        </span>
                      </td>
                      {!locked && (
                        <td className={ADMIN_TD_CLASS}>
                          <AdminRowActions>
                            {deleted ? (
                              <AdminRowAction
                                kind="restore"
                                label={`Restore ${row.promoCode}`}
                                onClick={() => void handleRestore(row)}
                              />
                            ) : (
                              <>
                                <AdminRowAction kind="edit" label={`Edit ${row.promoCode}`} onClick={() => setEditItem(row)} />
                                <AdminRowAction kind="delete" label={`Delete ${row.promoCode}`} onClick={() => setDeleteItem(row)} />
                              </>
                            )}
                          </AdminRowActions>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </AdminListTable>
              <PaginationFooter pagination={pagination} noun={['promo code', 'promo codes']} />
            </div>
          </div>
        )}

        {showCreate && (
          <PromoModal
            discounts={discounts}
            productTypes={productTypes}
            productCategories={productCategories}
            allPromos={allItems}
            onClose={() => setShowCreate(false)}
            onSaved={handleSaved}
          />
        )}

        {editItem && (
          <PromoModal
            promo={editItem}
            discounts={discounts}
            productTypes={productTypes}
            productCategories={productCategories}
            allPromos={allItems}
            onClose={() => setEditItem(null)}
            onSaved={handleSaved}
          />
        )}

        <ConfirmDialog
          isOpen={deleteItem !== null}
          title="Delete Promo Code"
          message={
            deleteItem
              ? `Are you sure you want to delete "${deleteItem.promoCode}"?`
              : ''
          }
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteItem(null)}
        />
      </AdminListPage>
    </div>
  );
}
